/* =========================================================================
   Local collectibles store ('catholics.collectibles') + the pure server/local
   MERGE used by the Collections screen. Relic redemptions and Pilgrimage step
   rewards earn Apostle/Relic collectibles here; guests + offline players keep a
   full local copy, accounts additionally grant server-side (api.grantCollectible)
   and reconcile on sign-in.

   The merge is a MAX-merge: an item is "earned" if EITHER the server row carries
   an earnedUtc OR the device has a local earn for that key — so a just-earned
   guest badge is never hidden by a stale server read, and a server earn shows for
   accounts even before the local mirror catches up.
   ========================================================================= */
import { track } from './telemetry';
import { grantCollectible } from './api';
import type { KVStore } from './dailyBlessing';
import type { CollectionItem, CollectionSet } from './api';
import { BUNDLED_SETS } from './data/collectionsData';
import { readFeasts } from './feasts';

const KEY = 'catholics.collectibles';

/** itemKey → earnedUtc (ISO). */
export type CollectibleMap = Record<string, string>;

function deviceStore(): KVStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function readCollectibles(store: KVStore | null = deviceStore()): CollectibleMap {
  try {
    if (!store) return {};
    const raw = store.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as CollectibleMap) : {};
  } catch {
    return {};
  }
}

function writeCollectibles(map: CollectibleMap, store: KVStore | null): void {
  try {
    if (store) store.setItem(KEY, JSON.stringify(map));
  } catch {
    /* private mode — collectible simply won't persist */
  }
}

export function hasCollectible(itemKey: string, store: KVStore | null = deviceStore()): boolean {
  return !!readCollectibles(store)[itemKey];
}

/** Earn a collectible locally + emit CollectibleEarned{key}; accounts also grant
    server-side (idempotent, fire-and-forget). Returns true only on the first earn
    so callers celebrate exactly once. */
export function earnCollectible(itemKey: string, now: Date = new Date(), store: KVStore | null = deviceStore()): boolean {
  const map = readCollectibles(store);
  if (map[itemKey]) return false;
  map[itemKey] = now.toISOString();
  writeCollectibles(map, store);
  track('CollectibleEarned', { key: itemKey });
  void grantCollectible({ itemKey }); // authed → server; guest → no-op
  return true;
}

/* ------------------------------- pure merge ------------------------------- */

/** Resolve one item's earned timestamp: server earn wins if present, else the
    local earn, else null (locked). PURE. */
export function mergeEarned(serverEarnedUtc: string | null, localEarnedUtc: string | null): string | null {
  return serverEarnedUtc ?? localEarnedUtc ?? null;
}

/** Merge the server collection catalog (or the bundled fallback when the server
    is unreachable) with the device's local earnings + local feast badges into the
    render model. PURE over its inputs. */
export function mergeCollections(
  server: CollectionSet[] | null,
  localCollectibles: CollectibleMap,
  localFeasts: Record<string, { earnedUtc: string; title: string; date?: string }>,
): CollectionSet[] {
  const base = server && server.length > 0 ? server : BUNDLED_SETS;
  return base.map((set) => {
    const items: CollectionItem[] = set.items.map((it) => ({
      ...it,
      earnedUtc: mergeEarned(it.earnedUtc, localCollectibles[it.key] ?? null),
    }));

    // The Feast Days set: offline the server carries no items, so surface any
    // locally-earned feast badges (which the server list would otherwise supply).
    if (set.key === 'feast_days') {
      const present = new Set(items.map((i) => i.key));
      for (const [k, v] of Object.entries(localFeasts)) {
        if (present.has(k)) continue;
        items.push({ key: k, title: v.title, icon: '🎉', earnedUtc: v.earnedUtc });
      }
    }
    return { ...set, items };
  });
}

/** Convenience wrapper reading the live device stores (used by the screen). */
export function mergeLive(server: CollectionSet[] | null): CollectionSet[] {
  return mergeCollections(server, readCollectibles(), readFeasts());
}
